import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Crown,
  LayoutGrid,
  MapPin,
  Search,
  Sparkles,
  SlidersHorizontal,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LazyMount } from "@/components/lazy-mount";
import { RecommendedAds } from "@/components/recommended-ads";
import { recommend } from "@/lib/recommendations";
import { AdCard } from "@/components/ad-card";
import { CategoryIcon } from "@/components/category-icon";
import { AllCategoriesSheet } from "@/components/all-categories-sheet";
import { cities, type Ad } from "@/lib/data";
import { useCategoryTree } from "@/lib/categories";
import { fetchActiveListings, fetchActiveListingsLimited } from "@/lib/listings";
import { SEO_DEFAULTS, fetchSeoSettings } from "@/lib/seo-settings";
import storeBannerImg from "@/assets/promo/store.webp";


/** Hər kateqoriya kafeli üçün 3D rəng çaları */
const tileHues = [255, 25, 145, 70, 300, 200, 350, 120, 40, 275, 175, 15];

const tileStyle = (i: number) =>
  ({ "--tile-h": String(tileHues[i % tileHues.length]) }) as React.CSSProperties;

const gridClass =
  "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-4";

/** Elan şəbəkəsi — 2 sütun, aşağıya doğru davam edir */
function AdRow({ ads }: { ads: Ad[] }) {
  return (
    <div className={gridClass}>
      {ads.map((ad, i) => (
        <AdCard key={ad.id} ad={ad} index={i} />
      ))}
    </div>
  );
}

export const Route = createFileRoute("/")({
  loader: async () => {
    // SSR-də elanları da gətiririk: HTML-də görünür (SEO/AI agentlər) və müştəri tərəfdə əlavə gözləmə olmur
    const [seo, ads] = await Promise.all([
      fetchSeoSettings(),
      // İlk açılış üçün daha yüngül SSR: qalan elanlar hidrasiyadan sonra gəlir
      fetchActiveListingsLimited(48).catch(() => [] as Ad[]),
    ]);
    return { seo, ads };
  },
  head: ({ loaderData }) => {
    const seo = loaderData?.seo ?? SEO_DEFAULTS;
    const title = seo["home_title"] || SEO_DEFAULTS["home_title"]!;
    const description = seo["home_description"] || SEO_DEFAULTS["home_description"]!;
    const keywords = [seo["home_keywords"], seo["global_keywords"]].filter(Boolean).join(", ");
    return {
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },

      { property: "og:url", content: "https://albazar.az/" },
      {
        property: "og:image",
        content: "https://albazar.az/pwa-icon-512-v3.png",
      },
      {
        name: "twitter:image",
        content: "https://albazar.az/pwa-icon-512-v3.png",
      },
      { property: "og:site_name", content: "Albazar.az" },
      { property: "og:locale", content: "az_AZ" },
      { name: "keywords", content: keywords },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Albazar.az",
          inLanguage: "az",
          url: "https://albazar.az/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://albazar.az/elanlar?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Albazar.az",
          url: "https://albazar.az/",
          logo: "https://albazar.az/pwa-icon-512-v3.png",
          areaServed: "AZ",
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+994776030335",
              contactType: "customer support",
              availableLanguage: ["az"],
            },
          ],
        }),
      },
    ],
    };
  },
  component: Home,
});

function Section({
  title,
  subtitle,
  children,
  to,
  icon,
  linkLabel,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  to?: string;
  icon?: React.ReactNode;
  linkLabel?: string;
}) {
  return (
    <section className="container-x mt-7">
      <div className="mb-2.5 flex items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-muted-foreground sm:text-base">
            {icon}
            {title}
          </h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {to && (
          <Link
            to="/elanlar"
            search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}
            className="flex shrink-0 items-center gap-0.5 text-sm font-semibold text-info hover:underline"
          >
            {linkLabel ?? "Hamısına bax"}
            <ChevronRight className="size-4" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function Home() {
  const navigate = useNavigate();
  const { main: categories } = useCategoryTree();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");

  const { ads: initialAds } = Route.useLoaderData();

  const { data: dbAds } = useQuery({
    queryKey: ["listings", "active"],
    queryFn: fetchActiveListings,
    initialData: initialAds,
    // SSR məlumatı təzə sayılır — ilk açılışda əlavə sorğu olmur;
    // tam siyahı 30 saniyədən sonra və ya növbəti keçiddə yenilənir
    initialDataUpdatedAt: Date.now(),
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const allAds: Ad[] = useMemo(() => dbAds ?? [], [dbAds]);

  /** Mağazalara prioritet verən sıralama köməkçisi */
  const sortByStore = (ads: Ad[]) =>
    [...ads].sort((a, b) => {
      const aStore = a.isStore ? 1 : 0;
      const bStore = b.isStore ? 1 : 0;
      if (bStore !== aStore) return bStore - aStore;
      return +new Date(b.createdAt) - +new Date(a.createdAt);
    });

  const vipAds = useMemo(() => sortByStore(allAds.filter((a) => a.badge === "vip")).slice(0, 8), [allAds]);
  const premiumAds = useMemo(
    () => sortByStore(allAds.filter((a) => a.badge === "premium")).slice(0, 8),
    [allAds],
  );
  const latestAds = useMemo(
    () => sortByStore(allAds).slice(0, 12),
    [allAds],
  );
  const videoAds = useMemo(
    () => sortByStore(allAds.filter((a) => Boolean(a.videoUrl) && a.isStore)).slice(0, 12),
    [allAds],
  );
  const storeAds = useMemo(() => sortByStore(allAds.filter((a) => a.isStore)).slice(0, 8), [allAds]);

  const newAds = useMemo(
    () =>
      sortByStore(allAds.filter((a) => a.badge === "none")).slice(0, 10),
    [allAds],
  );

  const [pickedCat, setPickedCat] = useState("");
  const [viewMode, setViewMode] = useState<"2" | "4" | "store">("2");

  const pickedAds = useMemo(() => {
    let list = allAds;
    if (pickedCat) list = list.filter((a) => a.category === pickedCat);
    if (viewMode === "store") list = list.filter((a) => a.isStore);
    return sortByStore(list).slice(0, viewMode === "4" ? 16 : 12);
  }, [allAds, pickedCat, viewMode]);


  const search = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({
      to: "/elanlar",
      search: { q, kateqoriya: "", seher: "", sirala: "yeni" },
    });
  };

  return (
    <div className="pb-6">
      <section className="container-x pt-3">
        <h1 className="sr-only">Albazar.az — pulsuz elan saytı</h1>
        <form
          onSubmit={search}
          className="relative flex items-center gap-2 rounded-full border border-border/60 bg-card p-1.5 pl-4 shadow-card"
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Axtar..."
            aria-label="Axtarış"
            className="h-11 flex-1 border-0 bg-transparent pl-1 pr-14 text-base shadow-none focus-visible:ring-0"
          />
          <Button
            type="submit"
            aria-label="Axtar"
            className="absolute right-1 top-1/2 size-11 -translate-y-1/2 shrink-0 rounded-full bg-info p-0 text-white hover:bg-info/90"
          >
            <Search className="size-5" />
          </Button>
        </form>


        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex min-w-0 flex-1 items-center rounded-full border border-border/60 bg-card px-3 py-2 shadow-card">
            <MapPin className="pointer-events-none size-3.5 shrink-0 text-info" />
            <select
              value={city}
              onChange={(e) => {
                const v = e.target.value;
                setCity(v);
                navigate({
                  to: "/elanlar",
                  search: { q: "", kateqoriya: "", seher: v, sirala: "yeni" },
                });
              }}
              aria-label="Şəhər seç"
              className="min-w-0 flex-1 appearance-none bg-transparent px-2 text-xs font-semibold text-info outline-none"
            >
              <option value="">Bütün şəhərlər</option>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none size-3.5 shrink-0 text-info" />
          </div>
          <Link
            to="/elanlar"
            search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-2 text-xs font-semibold text-info shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated"
          >
            <SlidersHorizontal className="size-3.5" /> Filtrlər
          </Link>
        </div>

        <nav aria-label="Kateqoriyalar" className="mt-4">
          <ul className="-mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-2 sm:-mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            <li className="shrink-0 snap-start">
              <AllCategoriesSheet>
                <button
                  type="button"
                  aria-label="Bütün Kateqoriyalar"
                  className="group flex w-[72px] flex-col items-center gap-1 rounded-xl border border-border/60 bg-card p-1.5 text-center shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated"
                >
                  <span className="grid size-9 place-items-center text-info">
                    <LayoutGrid className="size-7" strokeWidth={0} fill="currentColor" />
                  </span>
                  <span className="line-clamp-2 text-[9px] font-semibold leading-tight text-foreground">
                    Bütün
                  </span>
                </button>
              </AllCategoriesSheet>
            </li>
            {categories.map((c: { slug: string; name: string; icon: string }, i: number) => (
              <li key={c.slug} className="shrink-0 snap-start">
                <Link
                  to="/kateqoriya/$slug"
                  params={{ slug: c.slug }}
                  search={{ alt: "", marka: "" }}
                  className="group flex w-[72px] flex-col items-center gap-1 rounded-xl border border-border/60 bg-card p-1.5 text-center shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated"
                >
                  <span className="grid size-9 place-items-center">
                    <CategoryIcon
                      slug={c.slug}
                      name={c.name}
                      fallback={c.icon}
                      className="size-8 object-contain drop-shadow-[0_3px_5px_rgba(0,0,0,0.18)] transition-transform duration-300 group-hover:scale-110"
                    />
                  </span>
                  <span className="line-clamp-2 text-[9px] font-semibold leading-tight text-foreground">
                    {c.name}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </section>

      <section className="container-x mt-4">
        <Link
          to="/magazalar"
          className="group relative flex items-center gap-3 overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-r from-info/15 via-card to-primary/10 p-4 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated sm:p-6"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-info px-2.5 py-0.5 text-[10px] font-bold text-white">
              MAĞAZALAR
            </span>
            <span className="text-base font-extrabold leading-tight text-foreground sm:text-xl">
              Rəsmi mağazalar Albazar.az-da
            </span>
            <span className="text-[11px] font-medium leading-tight text-muted-foreground sm:text-sm">
              Etibarlı satıcılar, minlərlə məhsul — mağazalara bax
            </span>
          </span>
          <img
            src={storeBannerImg}
            alt=""
            loading="lazy"
            width={512}
            height={512}
            className="h-20 w-auto shrink-0 object-contain drop-shadow-[0_8px_14px_rgba(0,0,0,0.18)] transition-transform duration-300 group-hover:scale-105 sm:h-28"
          />
          <ChevronRight className="size-5 shrink-0 text-info" />
        </Link>
      </section>

      {storeAds.length > 0 && (
        <Section
          title="Mağaza elanları"
          subtitle="Təsdiqlənmiş mağazaların elanları"
          to="/magazalar"
          linkLabel="Mağazalar"
          icon={
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-4"
              preserveAspectRatio="none"
              aria-hidden="true"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id="storeSectionIconGradient"
                  x1="0"
                  y1="0"
                  x2="24"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#9333ea" />
                  <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
              </defs>
              <path
                d="M2.5 8.5a2 2 0 0 1 2-1.5h15a2 2 0 0 1 2 1.5v.5a4 4 0 0 1-4 4h-11a4 4 0 0 1-4-4z"
                stroke="url(#storeSectionIconGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d="M8 13v7M16 13v7M5 20h14"
                stroke="url(#storeSectionIconGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          }
        >
          <AdRow ads={storeAds} />
        </Section>
      )}

      <Section
        title="Digər kateqoriyalar"
        subtitle="Kateqoriya seç, görünüşü tənzimlə"
        icon={<LayoutGrid className="size-4 text-info" />}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex min-w-0 flex-1 items-center rounded-full border border-border/60 bg-card px-3 py-2 shadow-card">
            <select
              value={pickedCat}
              onChange={(e) => setPickedCat(e.target.value)}
              aria-label="Kateqoriya seç"
              className="min-w-0 flex-1 appearance-none bg-transparent px-1 text-xs font-semibold text-info outline-none"
            >
              <option value="">Bütün kateqoriyalar</option>
              {categories.map((c: { slug: string; name: string }) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none size-3.5 shrink-0 text-info" />
          </div>
          {(
            [
              { key: "2", label: "2 sütun" },
              { key: "4", label: "4 sütun" },
              { key: "store", label: "Mağaza elanları" },
            ] as const
          ).map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setViewMode(o.key)}
              aria-pressed={viewMode === o.key}
              className={`shrink-0 rounded-full border px-3 py-2 text-xs font-semibold shadow-card transition-all ${
                viewMode === o.key
                  ? "border-info bg-info text-white"
                  : "border-border/60 bg-card text-info hover:-translate-y-0.5"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {pickedAds.length > 0 ? (
          <div
            className={
              viewMode === "4"
                ? "grid grid-cols-4 gap-2 sm:gap-3"
                : "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-4"
            }
          >
            {pickedAds.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
            Bu seçim üzrə elan tapılmadı
          </div>
        )}
      </Section>




      {videoAds.length > 0 && (
        <Section
          title="Video elanlar"
          subtitle="Mağazaların video ilə paylaşdığı elanlar"
          to="/elanlar"
          icon={<Video className="size-4 text-info" />}
        >
          <div className={gridClass}>
            {videoAds.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} />
            ))}
          </div>
        </Section>
      )}


      <Section
        title="PREMIUM ELANLAR"
        to="/elanlar"
        linkLabel="Hamısına bax"
        icon={<Sparkles className="size-4 text-[oklch(0.55_0.2_300)]" />}
      >
        {premiumAds.length > 0 ? (
          <AdRow ads={premiumAds} />
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
            Hazırda Premium elan yoxdur
          </div>
        )}
      </Section>

      {vipAds.length > 0 && (
        <Section
          title="VIP elanlar"
          subtitle="Ən çox görünən seçilmiş elanlar"
          to="/elanlar"
          icon={<Crown className="size-4 text-primary" />}
        >
          <AdRow ads={vipAds} />
        </Section>
      )}

      {newAds.length > 0 && (
        <Section
          title="Yeni elanlar"
          subtitle="Bu gün əlavə olunanlar"
          to="/elanlar"
          icon={<Sparkles className="size-4 text-info" />}
        >
          <AdRow ads={newAds} />
        </Section>
      )}

      {latestAds.length > 0 && (
        <Section
          title="Son əlavə edilmişlər"
          subtitle="Ən yeni elanlar"
          to="/elanlar"
          icon={<Clock className="size-4 text-primary" />}
        >
          <div className={gridClass}>
            {latestAds.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} />
            ))}
          </div>

        </Section>
      )}

      <InfiniteFeed allAds={allAds} />

      <LazyMount minHeight={320} rootMargin="400px">
        <ForYou allAds={allAds} />
      </LazyMount>
    </div>
  );
}

/** Lalafo tərzi sonsuz lent — aşağı endikcə yeni elanlar yüklənir */
function InfiniteFeed({ allAds }: { allAds: Ad[] }) {
  const PAGE = 20;
  const [count, setCount] = useState(PAGE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const feedAds = useMemo(() => {
    // Mağaza elanlarına prioritet, sonra tarix
    return [...allAds].sort((a, b) => {
      const aStore = a.isStore ? 1 : 0;
      const bStore = b.isStore ? 1 : 0;
      if (bStore !== aStore) return bStore - aStore;
      return +new Date(b.createdAt) - +new Date(a.createdAt);
    });
  }, [allAds]);

  const visible = feedAds.slice(0, count);
  const hasMore = count < feedAds.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setCount((c) => Math.min(c + PAGE, feedAds.length));
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, feedAds.length]);

  if (!feedAds.length) return null;

  return (
    <Section
      title="Bütün elanlar"
      subtitle="Aşağı endikcə daha çox elan görünəcək"
      icon={<LayoutGrid className="size-4 text-info" />}
    >
      <div className={gridClass}>
        {visible.map((ad, i) => (
          <AdCard key={ad.id} ad={ad} index={i % PAGE} />
        ))}
      </div>
      <div ref={sentinelRef} className="flex justify-center py-6">
        {hasMore ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="size-4 animate-spin rounded-full border-2 border-info border-t-transparent" />
            Elanlar yüklənir…
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Bütün elanlar göstərildi</span>
        )}
      </div>
    </Section>
  );
}

function ForYou({ allAds }: { allAds: Ad[] }) {
  const [items, setItems] = useState<Ad[]>([]);
  useEffect(() => {
    setItems(recommend(allAds, { limit: 8 }));
  }, [allAds]);
  if (!items.length) return null;
  return (
    <div className="container-x">
      <RecommendedAds ads={items} subtitle="Son baxdığınız elanlara əsasən" />
    </div>
  );
}
